import { useMemo } from 'react';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/adminClient';

/**
 * Permissions are table-driven (role_permissions) with one explicit short-circuit:
 * the `super_admin` role is treated as god-mode and granted every permission key.
 * Every other role's powers are exactly the union of permission_keys assigned to
 * its roles in the role_permissions table.
 *
 * If you need a non-super role to have more access, grant it those permission
 * keys in the role_permissions table — do NOT add additional bypasses here.
 */
export function usePermissions() {
  const { adminUser } = useAdminAuth();
  const roles = adminUser?.roles || [];
  const isSuperAdmin = roles.includes('super_admin');

  const { data: rolePermissions = [], isLoading } = useQuery({
    queryKey: ['role-permissions', roles.join(',')],
    queryFn: async () => {
      if (!roles.length) return [];
      const { data } = await supabase
        .from('role_permissions')
        .select('permission_key')
        .in('role', roles);
      return (data || []).map(r => r.permission_key);
    },
    enabled: roles.length > 0 && !isSuperAdmin,
    staleTime: 5 * 60 * 1000,
  });

  const permissionSet = useMemo(() => new Set(rolePermissions), [rolePermissions]);

  // super_admin short-circuit — always granted, never blocked.
  // During loading, never deny — wait for resolution.
  const hasPermission = (key: string) =>
    isSuperAdmin || isLoading || permissionSet.has(key);
  const hasAnyPermission = (...keys: string[]) =>
    isSuperAdmin || isLoading || keys.some(k => permissionSet.has(k));

  return {
    hasPermission,
    hasAnyPermission,
    permissions: rolePermissions,
    /**
     * @deprecated Use hasPermission() with a specific key. Retained for
     * backward compatibility; reflects the super_admin short-circuit.
     */
    isSuperRole: isSuperAdmin,
    isLoading: isSuperAdmin ? false : isLoading,
  };
}
